import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'

import { axiosClient } from '../../../core/axios-client'

// Сотрудники парка: GET/POST/PATCH /companies/{id}/staff, шаблоны ролей,
// справочник прав и приглашения по ссылке. Всё закрыто правом staff_manage.
const staffUrl = (companyId, tail = '') => `/companies/${companyId}/staff${tail}`

const rejectWith = (err, rejectWithValue) => rejectWithValue(err.response?.data || err.message)

export const fetchStaff = createAsyncThunk('fetchStaff', async ({ companyId }, { rejectWithValue }) => {
  try {
    return (await axiosClient.get(staffUrl(companyId))).data
  } catch (err) {
    return rejectWith(err, rejectWithValue)
  }
})

export const fetchStaffRoles = createAsyncThunk('fetchStaffRoles', async ({ companyId }, { rejectWithValue }) => {
  try {
    return (await axiosClient.get(staffUrl(companyId, '/roles'))).data
  } catch (err) {
    return rejectWith(err, rejectWithValue)
  }
})

export const fetchStaffPermissions = createAsyncThunk(
  'fetchStaffPermissions',
  async ({ companyId }, { rejectWithValue }) => {
    try {
      return (await axiosClient.get(staffUrl(companyId, '/permissions'))).data
    } catch (err) {
      return rejectWith(err, rejectWithValue)
    }
  }
)

export const fetchStaffInvitations = createAsyncThunk(
  'fetchStaffInvitations',
  async ({ companyId }, { rejectWithValue }) => {
    try {
      return (await axiosClient.get(staffUrl(companyId, '/invitations'))).data
    } catch (err) {
      return rejectWith(err, rejectWithValue)
    }
  }
)

// Добавить уже зарегистрированного человека: по телефону или по id
export const addStaffMember = createAsyncThunk(
  'addStaffMember',
  async ({ companyId, ...body }, { rejectWithValue }) => {
    try {
      return (await axiosClient.post(staffUrl(companyId), body)).data
    } catch (err) {
      return rejectWith(err, rejectWithValue)
    }
  }
)

// role_code, overrides ({код: true | false | null}) и is_active - любое из них
export const updateStaffMember = createAsyncThunk(
  'updateStaffMember',
  async ({ companyId, userId, ...body }, { rejectWithValue }) => {
    try {
      return (await axiosClient.patch(staffUrl(companyId, `/${userId}`), body)).data
    } catch (err) {
      return rejectWith(err, rejectWithValue)
    }
  }
)

export const createStaffInvitation = createAsyncThunk(
  'createStaffInvitation',
  async ({ companyId, ...body }, { rejectWithValue }) => {
    try {
      return (await axiosClient.post(staffUrl(companyId, '/invitations'), body)).data
    } catch (err) {
      return rejectWith(err, rejectWithValue)
    }
  }
)

export const revokeStaffInvitation = createAsyncThunk(
  'revokeStaffInvitation',
  async ({ companyId, code }, { rejectWithValue }) => {
    try {
      await axiosClient.delete(staffUrl(companyId, `/invitations/${code}`))
      return code
    } catch (err) {
      return rejectWith(err, rejectWithValue)
    }
  }
)

const upsertMember = (items, card) => {
  const index = items.findIndex((item) => item.user_id === card.user_id)
  if (index === -1) return [...items, card]
  const next = [...items]
  next[index] = card
  return next
}

const initialState = {
  items: [],
  roles: [],
  permissions: [],
  invitations: [],
  loading: false,
  // Ответ 403 на список значит «вы не владелец»: показываем это, а не пустую таблицу
  forbidden: false,
  invitationsLoading: false,
}

export const slice = createSlice({
  name: 'staff',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(fetchStaff.pending, (state) => {
      state.loading = true
      state.forbidden = false
    })
    builder.addCase(fetchStaff.fulfilled, (state, { payload }) => {
      state.items = payload
      state.loading = false
    })
    builder.addCase(fetchStaff.rejected, (state, { payload }) => {
      state.loading = false
      state.forbidden = payload?.code === 'permission_denied'
    })
    builder.addCase(fetchStaffRoles.fulfilled, (state, { payload }) => {
      state.roles = payload
    })
    builder.addCase(fetchStaffPermissions.fulfilled, (state, { payload }) => {
      state.permissions = payload
    })
    builder.addCase(fetchStaffInvitations.pending, (state) => {
      state.invitationsLoading = true
    })
    builder.addCase(fetchStaffInvitations.fulfilled, (state, { payload }) => {
      state.invitations = payload
      state.invitationsLoading = false
    })
    builder.addCase(fetchStaffInvitations.rejected, (state) => {
      state.invitationsLoading = false
    })
    builder.addCase(addStaffMember.fulfilled, (state, { payload }) => {
      state.items = upsertMember(state.items, payload)
    })
    builder.addCase(updateStaffMember.fulfilled, (state, { payload }) => {
      // reset_overrides_count - служебное поле ответа на смену шаблона
      const { reset_overrides_count: _reset, ...card } = payload
      state.items = upsertMember(state.items, card)
    })
    builder.addCase(createStaffInvitation.fulfilled, (state, { payload }) => {
      state.invitations = [payload, ...state.invitations]
    })
    builder.addCase(revokeStaffInvitation.fulfilled, (state, { payload }) => {
      state.invitations = state.invitations.map((item) =>
        item.code === payload ? { ...item, status: 'revoked' } : item
      )
    })
  },
})

export default slice.reducer
